import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import './ToolkitTab.css';

const FILE_ICONS = {
  pdf: '📄',
  doc: '📝',
  docx: '📝',
  xls: '📊',
  xlsx: '📊',
  png: '🖼️',
  jpg: '🖼️',
  jpeg: '🖼️',
  gif: '🖼️',
  mp4: '🎥',
  mov: '🎥',
  mp3: '🎵',
  txt: '📃',
};

function getFileIcon(fileType) {
  return FILE_ICONS[fileType?.toLowerCase()] || '📎';
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ToolkitTab({ clientId, client }) {
  const { user } = useAuth();
  const [practitionerId, setPractitionerId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadScope, setUploadScope] = useState('client'); // 'client' or 'global'
  const [uploadName, setUploadName] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    loadDocs();
  }, [clientId]);

  async function loadDocs() {
    setLoading(true);

    // Get practitioner id
    const { data: prac } = await supabase
      .from('practitioners')
      .select('id')
      .eq('auth_user_id', user.id)
      .single();

    if (prac) setPractitionerId(prac.id);

    // Load all docs for this practitioner (global + this client)
    const { data } = await supabase
      .from('toolkit_documents')
      .select('*')
      .eq('practitioner_id', prac?.id)
      .or(`client_id.is.null,client_id.eq.${clientId}`)
      .order('sort_order')
      .order('created_at', { ascending: false });

    setDocs(data || []);
    setLoading(false);
  }

  function resetUpload() {
    setShowUpload(false);
    setUploadName('');
    setUploadDesc('');
    setUploadFile(null);
    setUploadScope('client');
  }

  async function handleUpload() {
    if (!uploadFile || !practitionerId) return;
    setUploading(true);

    try {
      const fileExt = uploadFile.name.split('.').pop().toLowerCase();
      const fileName = `${practitionerId}/${Date.now()}_${uploadFile.name}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('toolkit')
        .upload(fileName, uploadFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('toolkit')
        .getPublicUrl(fileName);

      // Create toolkit_documents record
      const { data: doc, error: docError } = await supabase
        .from('toolkit_documents')
        .insert({
          practitioner_id: practitionerId,
          client_id: uploadScope === 'client' ? clientId : null,
          name: uploadName.trim() || uploadFile.name,
          description: uploadDesc.trim() || null,
          file_url: urlData.publicUrl,
          file_type: fileExt,
          file_size: uploadFile.size,
          sort_order: docs.length,
        })
        .select()
        .single();

      if (docError) throw docError;

      if (doc) {
        setDocs((prev) => [doc, ...prev]);
      }

      resetUpload();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  async function removeDoc(docId, fileUrl) {
    if (!window.confirm('Remove this document? The file will be deleted.')) return;

    // Delete storage file
    try {
      const path = fileUrl.split('/toolkit/')[1];
      if (path) {
        await supabase.storage.from('toolkit').remove([decodeURIComponent(path)]);
      }
    } catch (err) {
      console.error('Storage delete error:', err);
    }

    // Delete record
    await supabase.from('toolkit_documents').delete().eq('id', docId);
    setDocs((prev) => prev.filter((d) => d.id !== docId));
  }

  const globalDocs = docs.filter((d) => !d.client_id);
  const clientDocs = docs.filter((d) => d.client_id === clientId);

  if (loading) {
    return <div className="tab-loading">Loading toolkit...</div>;
  }

  return (
    <div className="tk-tab">
      <div className="tk-tab-header">
        <div>
          <h3 className="tk-tab-title">Toolkit</h3>
          <p className="tk-tab-subtitle">
            Upload reference documents for {client.name.split(' ')[0]}. Global docs are visible to all your clients.
          </p>
        </div>
        <button className="tk-upload-btn" onClick={() => setShowUpload(true)}>
          + Upload
        </button>
      </div>

      {/* Upload form */}
      {showUpload && (
        <div className="tk-upload-card">
          <h4 className="tk-upload-title">Upload Document</h4>

          <div className="tk-upload-scope">
            <button
              className={`tk-scope-btn ${uploadScope === 'client' ? 'tk-scope-active' : ''}`}
              onClick={() => setUploadScope('client')}
            >
              For {client.name.split(' ')[0]} only
            </button>
            <button
              className={`tk-scope-btn ${uploadScope === 'global' ? 'tk-scope-active' : ''}`}
              onClick={() => setUploadScope('global')}
            >
              All clients (global)
            </button>
          </div>

          <div className="form-group">
            <label>File *</label>
            <input
              type="file"
              className="form-input tk-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setUploadFile(f);
                  if (!uploadName) setUploadName(f.name.replace(/\.[^/.]+$/, ''));
                }
              }}
            />
          </div>

          <div className="form-group">
            <label>Display Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Gut Healing Protocol Guide"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Description (optional)</label>
            <input
              type="text"
              className="form-input"
              placeholder="Brief description of the document"
              value={uploadDesc}
              onChange={(e) => setUploadDesc(e.target.value)}
            />
          </div>

          <div className="tk-upload-actions">
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={!uploadFile || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
            <button className="btn btn-secondary" onClick={resetUpload}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Client-specific docs */}
      <div className="tk-section">
        <h4 className="tk-section-title">
          For {client.name.split(' ')[0]}
          <span className="tk-section-count">{clientDocs.length}</span>
        </h4>
        {clientDocs.length === 0 ? (
          <p className="tk-empty">No documents uploaded for this client yet.</p>
        ) : (
          <div className="tk-doc-list">
            {clientDocs.map((doc) => (
              <div key={doc.id} className="tk-doc-card">
                <span className="tk-doc-icon">{getFileIcon(doc.file_type)}</span>
                <div className="tk-doc-info">
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="tk-doc-name">
                    {doc.name}
                  </a>
                  {doc.description && <p className="tk-doc-desc">{doc.description}</p>}
                  <p className="tk-doc-meta">
                    {doc.file_type?.toUpperCase()}{doc.file_size ? ` · ${formatFileSize(doc.file_size)}` : ''}
                  </p>
                </div>
                <button className="tk-doc-remove" onClick={() => removeDoc(doc.id, doc.file_url)} title="Remove">
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Global docs */}
      <div className="tk-section">
        <h4 className="tk-section-title">
          Global (all clients)
          <span className="tk-section-count">{globalDocs.length}</span>
        </h4>
        {globalDocs.length === 0 ? (
          <p className="tk-empty">No global documents yet.</p>
        ) : (
          <div className="tk-doc-list">
            {globalDocs.map((doc) => (
              <div key={doc.id} className="tk-doc-card">
                <span className="tk-doc-icon">{getFileIcon(doc.file_type)}</span>
                <div className="tk-doc-info">
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="tk-doc-name">
                    {doc.name}
                  </a>
                  {doc.description && <p className="tk-doc-desc">{doc.description}</p>}
                  <p className="tk-doc-meta">
                    {doc.file_type?.toUpperCase()}{doc.file_size ? ` · ${formatFileSize(doc.file_size)}` : ''}
                  </p>
                </div>
                <button className="tk-doc-remove" onClick={() => removeDoc(doc.id, doc.file_url)} title="Remove">
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
